import type { OutreachFunnelSteps, OutreachKpis, OutreachStageMix } from '@discovr/types';
import { formatShare, splitPercents, type BreakdownPart } from './crmFormat';

const UNKNOWN = '—';
const FULL_WIDTH_PCT = 100;

/** Which text colour reads on a funnel fill: the top steps are pale washes, the rest solid violet. */
type FillContrast = 'on-light' | 'on-dark';

interface FunnelStepSpec {
  key: keyof OutreachFunnelSteps;
  label: string;
  fill: string;
  contrast: FillContrast;
}

/** Figma's funnel, widest to narrowest; fills darken step by step to draw the eye to the bottleneck. */
const FUNNEL_STEPS: readonly FunnelStepSpec[] = [
  { key: 'candidates', label: 'Candidates', fill: 'var(--crm-funnel-1)', contrast: 'on-light' },
  { key: 'reachable', label: 'Reachable', fill: 'var(--crm-funnel-2)', contrast: 'on-light' },
  { key: 'contacted', label: 'Contacted', fill: 'var(--crm-funnel-3)', contrast: 'on-dark' },
  { key: 'replied', label: 'Replied', fill: 'var(--crm-funnel-4)', contrast: 'on-dark' },
  { key: 'rateLocked', label: 'Rate locked', fill: 'var(--crm-funnel-5)', contrast: 'on-dark' },
  { key: 'withBrand', label: 'With brand', fill: 'var(--crm-funnel-6)', contrast: 'on-dark' },
  { key: 'signed', label: 'Signed', fill: 'var(--crm-funnel-7)', contrast: 'on-dark' },
];

/** Steps held back until the close step exists; empty this list to show them again. */
export const HIDDEN_FUNNEL_STEPS: readonly (keyof OutreachFunnelSteps)[] = ['signed'];

const VISIBLE_FUNNEL_STEPS = FUNNEL_STEPS.filter((step) => !HIDDEN_FUNNEL_STEPS.includes(step.key));

export interface FunnelBarModel {
  key: keyof OutreachFunnelSteps;
  label: string;
  /** null when the step is not measured yet (Signed until the close is built). */
  count: number | null;
  /** Share of candidates for display: "46%", "<1%", or "—". */
  share: string;
  /** Bar width as a percentage of the track, 0–100. */
  widthPct: number;
  fill: string;
  contrast: FillContrast;
}

const widthOf = (count: number | null, base: number): number =>
  count === null || base <= 0 ? 0 : Math.min(FULL_WIDTH_PCT, (count / base) * FULL_WIDTH_PCT);

/** Every step as a share of candidates. */
export function funnelBars(steps: OutreachFunnelSteps): FunnelBarModel[] {
  return VISIBLE_FUNNEL_STEPS.map(({ key, label, fill, contrast }) => {
    const count = steps[key];
    return {
      key,
      label,
      count,
      share: count === null ? UNKNOWN : formatShare(count, steps.candidates),
      widthPct: widthOf(count, steps.candidates),
      fill,
      contrast,
    };
  });
}

/** The funnel has something to draw: at least one candidate recorded. */
export const hasFunnelCandidates = (steps: Pick<OutreachFunnelSteps, 'candidates'>): boolean => steps.candidates > 0;

export function funnelSummary(bars: readonly FunnelBarModel[]): string {
  const items = bars.map((bar) => (bar.count === null ? `${bar.label} not measured yet` : `${bar.label} ${bar.count} (${bar.share})`));
  return `Funnel: ${items.join(', ')}.`;
}

type StageBucketKey = keyof Pick<OutreachStageMix, 'notContacted' | 'inFlight' | 'inConversation' | 'lockedSigned' | 'closed'>;

const STAGE_BUCKETS: readonly { key: StageBucketKey; label: string; color: string }[] = [
  { key: 'notContacted', label: 'Not contacted', color: 'var(--crm-stage-not-contacted)' },
  { key: 'inFlight', label: 'In flight', color: 'var(--crm-stage-in-flight)' },
  { key: 'inConversation', label: 'In conversation', color: 'var(--crm-stage-in-conversation)' },
  { key: 'lockedSigned', label: 'Locked / signed', color: 'var(--crm-stage-locked)' },
  { key: 'closed', label: 'Suppressed / closed', color: 'var(--crm-stage-closed)' },
];

export interface StageSegmentModel extends BreakdownPart {
  key: StageBucketKey;
  color: string;
  /** Legend share at 2 decimals; the five always add up to 100. */
  percent: number;
  /** Exact bar width, 0–100. */
  widthPct: number;
}

export function stageSegments(mix: OutreachStageMix): StageSegmentModel[] {
  const counts = STAGE_BUCKETS.map(({ key }) => mix[key]);
  const percents = splitPercents(counts);
  const sum = counts.reduce((total, count) => total + count, 0);
  return STAGE_BUCKETS.map(({ key, label, color }, index) => {
    const count = mix[key];
    return { key, label, color, count, percent: percents[index] ?? 0, widthPct: widthOf(count, sum) };
  });
}

/** Every creator in this campaign's outreach; null when an older Go sends no stage mix, so nothing claims "none". */
export function creatorsTotal(kpis: Pick<OutreachKpis, 'stageMix'>): number | null {
  return kpis.stageMix?.total ?? null;
}

/** Contact to signed stays empty until signing is built; an older Go cannot say, so it reads the same. */
export const isSigningPending = (kpis: Pick<OutreachKpis, 'funnelSteps'>): boolean => (kpis.funnelSteps?.signed ?? null) === null;
